import { IconButton, Tooltip, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight'
import moment from 'moment'
import NotesIcon from '@mui/icons-material/Notes'
import PreviewIcon from '@mui/icons-material/Preview'
import { ACTIVITY_TYPES } from '../../utility/constants'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import { useAuthContext } from '../../hooks/useAuthContext'
import { FileOpen } from '@mui/icons-material'
import AutoGeneratedDocumentList from './AutoGenerateDocumentList'

const timePassed = (startDatetime, endDatetime = new Date()) => {
  let diffMs = new Date(endDatetime).getTime() - new Date(startDatetime).getTime()

  const units = [
    { label: 'year', ms: 1000 * 60 * 60 * 24 * 365 },
    { label: 'month', ms: 1000 * 60 * 60 * 24 * 30 },
    { label: 'day', ms: 1000 * 60 * 60 * 24 },
    { label: 'hour', ms: 1000 * 60 * 60 },
    { label: 'minute', ms: 1000 * 60 },
  ]

  const result = []

  for (const u of units) {
    if (diffMs >= u.ms) {
      const value = Math.floor(diffMs / u.ms)
      diffMs -= value * u.ms
      result.push(`${value} ${u.label}${value > 1 ? 's' : ''}`)
    }
    if (result.length === 2) break // keep only 2 units
  }

  return result.length ? result.join(', ') : '1 minute'
}

function generateEdges(nodes) {
  const edges = []
  nodes?.forEach((node) => {
    node?.to_process_ids?.forEach((childId) => {
      edges.push({ from: node.id, to: childId })
    })
  })

  return edges
}

function computeLayout(nodes) {
  const edges = generateEdges(nodes)

  const incoming = {}
  const outgoing = {}

  nodes.forEach((n) => {
    incoming[n.id] = []
    outgoing[n.id] = []
  })

  edges.forEach((e) => {
    incoming[e.to].push(e.from)
    outgoing[e.from].push(e.to)
  })

  const roots = nodes.filter((n) => incoming[n.id].length === 0).map((n) => n.id)

  const level = {}
  const queue = [...roots]

  roots.forEach((r) => (level[r] = 0))

  while (queue.length) {
    const current = queue.shift()

    outgoing[current].forEach((next) => {
      level[next] = Math.max(level[current] + 1, level[next] || 0)
      queue.push(next)
    })
  }

  const grouped = {}
  Object.entries(level).forEach(([id, lvl]) => {
    if (!grouped[lvl]) grouped[lvl] = []
    grouped[lvl].push(id)
  })

  const H_STEP = 320
  const V_STEP = 120

  const positions = {}

  Object.keys(grouped).forEach((lvl) => {
    grouped[lvl].forEach((id, i) => {
      positions[id] = {
        x: i * H_STEP, // grow horizontally by sibling index
        y: lvl * V_STEP, // grow vertically by level
      }
    })
  })

  return { positions, edges }
}

const TaskStatus = ({ status }) => {
  if (status === 'closed') {
    return (
      <Tooltip title="Closed" placement="top">
        <CheckCircleIcon style={{ fontSize: 12, marginRight: 4, color: 'green' }} />
      </Tooltip>
    )
  } else if (status === 'delegated') {
    return (
      <Tooltip title="Delegated" placement="top">
        <ArrowCircleRightIcon style={{ fontSize: 12, marginRight: 4, color: 'purple' }} />
      </Tooltip>
    )
  } else {
    return (
      <Tooltip title="Active" placement="top">
        <RadioButtonCheckedIcon
          style={{ fontSize: 12, marginRight: 4, color: 'red' }}
          color="red"
        />
      </Tooltip>
    )
  }
}

export default function Workflow({ nodes, openProcessData, openTask, docMap }) {
  const { user } = useAuthContext()
  const canvasRef = useRef(null)
  const [docData, setDocData] = useState([])
  const [isDocOpen, setIsDocOpen] = useState(false)
  const [selectedNode, setSelectedNode] = useState(false)

  const { positions, edges } = computeLayout(nodes)

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')

    // ---- FIX: AUTO EXPAND CANVAS ----
    let maxX = 0
    let maxY = 0

    Object.values(positions).forEach(({ x, y }) => {
      if (x > maxX) maxX = x
      if (y > maxY) maxY = y
    })

    // add padding so edges are not cut
    const padding = 300
    canvas.width = maxX + padding
    canvas.height = maxY + padding
    // ----------------------------------

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    edges.forEach((edge) => {
      const p1 = positions[edge.from]
      const p2 = positions[edge.to]

      if (!p1 || !p2) return

      const startX = p1.x + 70 // right side of source node
      const startY = p1.y + 35

      // endpoint is slightly before entering the node div
      const endX = p2.x + 70
      const endY = p2.y

      // choosing mid having higher X coordinate

      let midX = null
      let midY = null
      if (startX > endX) {
        midX = startX
        midY = endY
      } else {
        midX = endX
        midY = startY
      }

      ctx.beginPath()

      ctx.moveTo(startX, startY)
      ctx.lineTo(midX, midY)
      ctx.lineTo(endX, endY)

      ctx.lineWidth = 1
      ctx.stroke()

      // ---- DRAW ARROW HEAD AT END (visible before node) ----
      const arrowSize = 10

      // Direction vector of last segment
      const dx = endX - midX
      const dy = endY - midY
      const angle = Math.atan2(dy, dx)

      // Arrowhead point coordinates
      const arrowX1 = endX - arrowSize * Math.cos(angle - Math.PI / 6)
      const arrowY1 = endY - arrowSize * Math.sin(angle - Math.PI / 6)

      const arrowX2 = endX - arrowSize * Math.cos(angle + Math.PI / 6)
      const arrowY2 = endY - arrowSize * Math.sin(angle + Math.PI / 6)

      ctx.beginPath()
      ctx.moveTo(endX, endY)
      ctx.lineTo(arrowX1, arrowY1)
      ctx.lineTo(arrowX2, arrowY2)
      ctx.closePath()
      ctx.fill()
      // ------------------------------------------------------
    })
  }, [positions, edges])

  let showDocumentData = async (node) => {
    setIsDocOpen(true)
    setDocData(docMap[node.id])
    setSelectedNode(node)
  }

  return (
    <>
      <div
        style={{
          padding: '12px 0px',
          overflow: 'scroll',
          height: '100vh',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ position: 'relative' }}>
          <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, zIndex: 0 }} />

          {nodes.map((node) => {
            const pos = positions[node.id]
            return (
              <div
                key={node.id}
                style={{
                  position: 'absolute',
                  left: pos.x,
                  top: pos.y,
                  width: 220,
                  padding: 10,
                  background: '#fff',
                  border: '1px solid black',
                  borderRadius: 8,
                  zIndex: 1,
                }}
              >
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <TaskStatus status={node.status} />
                    <Typography variant="p" style={{ fontWeight: 'bold', fontSize: 12 }}>
                      {node.activity_template_name} {node.version ? '(v' + node.version + ')' : ''}
                    </Typography>
                  </div>
                  <Typography variant="p" style={{ fontSize: 12 }}>
                    {node.assignee}{' '}
                    {[
                      ACTIVITY_TYPES.END_ACCEPTED,
                      ACTIVITY_TYPES.END_REJECTED,
                      ACTIVITY_TYPES.PARALLEL_START,
                      ACTIVITY_TYPES.PARALLEL_END,
                    ].includes(node.activity_type)
                      ? ''
                      : `(${
                          node.status == 'active'
                            ? timePassed(node.created_at)
                            : timePassed(node.created_at, node.modified_at)
                        })`}
                  </Typography>
                  <br />
                  <Typography variant="p" style={{ fontSize: 10 }}>
                    {moment(node.created_at).format('DD MMM YYYY, hh:mm A')}
                  </Typography>

                  {/* Action Icon Buttons */}
                  <div style={{ position: 'absolute', bottom: 0, right: 0, background: 'white' }}>
                    <Tooltip title={node.overall_comments} placement="top">
                      <IconButton size="small">
                        <NotesIcon style={{ fontSize: 12 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Process Data" placement="top">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (
                            node.status !== 'delegated' &&
                            !node.is_parallel_activity &&
                            ![ACTIVITY_TYPES.PARALLEL_START, ACTIVITY_TYPES.PARALLEL_END].includes(
                              node.activity_type
                            )
                          ) {
                            openProcessData(node)
                          }
                        }}
                        disabled={
                          node.status === 'delegated' ||
                          node.is_parallel_activity ||
                          [ACTIVITY_TYPES.PARALLEL_START, ACTIVITY_TYPES.PARALLEL_END].includes(
                            node.activity_type
                          )
                        }
                      >
                        <PreviewIcon style={{ fontSize: 12 }} />
                      </IconButton>
                    </Tooltip>
                    {node.status == 'active' && user.id == node.modified_by ? (
                      <Tooltip title={'Work on Task'} placement="top">
                        <IconButton size="small" onClick={() => openTask(node)}>
                          <TaskAltIcon style={{ fontSize: 12 }} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <></>
                    )}
                    {docMap[node.id]?.length > 0 && (
                      <Tooltip title={'View auto generated files'} placement="top">
                        <IconButton size="small" onClick={() => showDocumentData(node)}>
                          <FileOpen style={{ fontSize: 12 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
      {isDocOpen && (
        <AutoGeneratedDocumentList
          isDocOpen={isDocOpen}
          setIsDocOpen={setIsDocOpen}
          title={'Auto Generated Document(s)'}
          docData={docData}
          selectedNode={selectedNode}
        />
      )}
    </>
  )
}
